
/**
 * Represents the theme settings for text elements in a chart.
 *
 * @type {Object} TextThemeType
 * @property {string} fontFamily - The font family to be used for the text.
 * @property {number} fontSize - The size of the font to be used for the text.
 * @property {Object} color - The color settings for the text.
 * @property {string} color.text - The primary color of the text.
 * @property {string} color.textSubtle - The color of the subtle text.
 */
type TextThemeType = {
    fontFamily: string;
    fontSize: number;
    color: {
        text: string;
        textSubtle: string;
    },
}

/**
 * Represents the theme configuration for a candlestick chart.
 *
 * @type {Object} CandleStickThemeType
 *
 * @property {string} upColor - The color used for the candlestick when the value goes up.
 * @property {string} downColor - The color used for the candlestick when the value goes down.
 * @property {string} borderDownColor - The color used for the border of the candlestick when the value goes down.
 * @property {string} borderUpColor - The color used for the border of the candlestick when the value goes up.
 * @property {string} wickDownColor - The color used for the wick of the candlestick when the value goes down.
 * @property {string} wickUpColor - The color used for the wick of the candlestick when the value goes up.
 */
type CandleStickThemeType = {
    upColor: string;
    downColor: string;
    borderDownColor: string;
    borderUpColor: string;
    wickDownColor: string;
    wickUpColor: string;
}


/**
 * Represents the theme configuration for a line chart.
 *
 * @typedef {Object} LineChartThemeType
 * @property {string} color - The color of the line in the chart.
 * @property {number} lineWidth - The width of the line in the chart.
 * @property {0 | 1 | 2} lineType - The type of the line in the chart.
 * - 0: simple
 * - 1: stepped
 * - 2: curved
 */
type LineChartThemeType = {
    color: string;
    lineWidth: number;
    lineType: 0 | 1 | 2; // 0 - simple, 1 - stepped, 2 - curved
}

/**
 * Represents the theme configuration for charts.
 *
 * @property {TextThemeType} text - The theme settings for text elements in the chart.
 * @property {CandleStickThemeType} candleStick - The theme settings for candlestick charts.
 * @property {LineChartThemeType} lineChart - The theme settings for line charts.
 */
interface ChartTheme {
    text: TextThemeType;
    candleStick: CandleStickThemeType;
    lineChart: LineChartThemeType;
}

/**
 * The default theme for charts.
 */
export const chartTheme: ChartTheme = {
    text: {
        fontFamily: "Kanit, sans-serif",
        fontSize: 12,
        color: {
            text: '#FFF',
            textSubtle: '#333',
        },
    },
    candleStick: {
        upColor: '#00ff5e',
        downColor: '#ff5e23',
        borderDownColor: '#ff5e23',
        borderUpColor: '#00ff5e',
        wickDownColor: '#d800c2',
        wickUpColor: '#00ffe5',
    },
    lineChart: {
        color: '#00ff5e',
        lineWidth: 3,
        lineType: 0,
    },
}
